import { Resolution } from './types';

export const round = (number: number, precision: number = 0) => {
  if (number % 2 === 0) {
    return number;
  }

  const multiplier = Math.pow(10, precision);
  return Math.round(number * multiplier) / multiplier;
};

/**
 * Rounds a number to the nearest even whole number
 * @param number - The number to round
 * @returns The number rounded to the nearest even whole number
 * @example roundToNearestEven(1991.3) => 1992
 * @example roundToNearestEven(1990.8) => 1990
 * @example roundToNearestEven(1991) => 1992
 * @example roundToNearestEven(2156.84) => 2156
 */
export const roundToNearestEven = (number: number): number => {
  const rounded = Math.round(number);

  // If already even, return it
  if (rounded % 2 === 0) {
    return rounded;
  }

  // For odd whole numbers, always round up to next even
  if (Number.isInteger(number)) {
    return rounded + 1;
  }

  // For non-integers, return nearest even number
  const floor = Math.floor(number);
  const ceil = Math.ceil(number);

  const lowerEven = floor % 2 === 0 ? floor : floor - 1;
  const upperEven = ceil % 2 === 0 ? ceil : ceil + 1;

  const distanceToLower = Math.abs(number - lowerEven);
  const distanceToUpper = Math.abs(number - upperEven);

  return distanceToLower < distanceToUpper ? lowerEven : upperEven;
};

/**
 * Rounds resolution values to a specified precision
 * @param resolution - The resolution to round
 * @param precision - Number of decimal places (optional)
 * @returns New resolution with rounded values
 */
export const roundResolutionValues = (
  resolution: Resolution,
  precision?: number
): Resolution => {
  return {
    width: round(resolution.width, precision),
    height: round(resolution.height, precision),
  };
};
